import { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { BsQuestionCircle } from 'react-icons/bs';

import BayCheckbox from '../../BayCheckbox';
import BaySelect from '../../BaySelect';
import BayTooltip from '../../BayTooltip';

interface AccessLevels {
	admin: string;
	staff: string;
	users: string;
}

interface OfficeGroup {
	label: string;
	value: string | number;
}

interface BayLimitersProps {
	currentData?: unknown;
	officeList: OfficeGroup[];
	groupList: OfficeGroup[];
	isSelectAccess: boolean;
	setIsSelectAccess: (value: boolean) => void;
	accessLevels: AccessLevels;
	setAccessLevels: (value: AccessLevels | ((prev: AccessLevels) => AccessLevels)) => void;
	isSelectOffices: boolean;
	setIsSelectOffices: (value: boolean) => void;
	selectedOfficesArr: OfficeGroup[];
	handleSelectLimiters: (name: string, selectedValue: unknown) => void;
	t: (key: string) => string;
	isEmptyAccess: boolean;
	setIsEmptyAccess: (value: boolean) => void;
	selectedAccessGroupArr: OfficeGroup[];
	isSelectGroups: boolean;
	setIsSelectGroups: (value: boolean) => void;
	isBanner?: boolean;
}

interface LinkAccessLevel {
	label: string;
	value: string;
	key: keyof AccessLevels;
	tooltipContent: string;
}

const BayLimiters = (props: BayLimitersProps) => {
	const {
		currentData,
		officeList,
		groupList,
		isSelectAccess,
		setIsSelectAccess,
		accessLevels,
		setAccessLevels,
		isSelectOffices,
		setIsSelectOffices,
		selectedOfficesArr,
		handleSelectLimiters,
		t,
		isEmptyAccess,
		setIsEmptyAccess,
		selectedAccessGroupArr,
		isSelectGroups,
		setIsSelectGroups,
		isBanner = false,
	} = props;

	const linkAccessLevels: LinkAccessLevel[] = [
		{
			label: 'Administrators ',
			value: 'A',
			key: 'admin',
			tooltipContent: 'adminAccessLevelNote',
		},
		{
			label: 'Staff ',
			value: 'S',
			key: 'staff',
			tooltipContent: 'staffAccessLevelNote',
		},
		{
			label: 'Users ',
			value: 'U',
			key: 'users',
			tooltipContent: 'userAccessLevelNote',
		},
	];

	useEffect(() => {
		const isEmptyAccessLevel = Object.values(accessLevels).every(
			(val) => val === '' || val === null,
		);
		if (isSelectAccess && isEmptyAccessLevel) {
			setIsEmptyAccess(true);
		} else {
			setIsEmptyAccess(false);
		}
	}, [isSelectAccess, accessLevels, setIsEmptyAccess]);

	return (
		<div>
			<Row className="mt-4">
				<Form.Label className="text-900">{t('accessLevels')}</Form.Label>
			</Row>
			<Row className="mt-0">
				<Form.Label className="fw-normal mb-0">
					{isBanner ? t('chooseAccessLevelBanner') : t('chooseAccessLevel')}
				</Form.Label>
				<Form.Group as={Col} className="mb-4">
					<div className="gap-2 flex-row align-items-center ">
						<BayCheckbox
							className="mt-3"
							id="all_Access"
							isChecked={!isSelectAccess}
							isRadio
							label={t('all')}
							name="accessLevels"
							value="All"
							onChangeFunc={() => {
								setAccessLevels({ admin: '', staff: '', users: '' });
								setIsSelectAccess(false);
							}}
						/>
						<BayCheckbox
							className="mt-2 pt-1"
							id="selectAccess"
							isChecked={isSelectAccess}
							isRadio
							label={t('selectLevels')}
							name="accessLevels"
							value="select"
							onChangeFunc={() => {
								setIsSelectAccess(true);
								setAccessLevels({ admin: 'A', staff: 'S', users: 'U' });
							}}
						/>
					</div>
					{isSelectAccess && (
						<>
							{linkAccessLevels.map((val, index) => (
								<div
									key={index}
									className="d-flex align-items-center ms-5 mt-3"
								>
									<BayCheckbox
										id={val.key}
										isChecked={
											currentData && accessLevels[val?.key] === val.value
												? true
												: false
										}
										label={val.label}
										name={val.key}
										value={val.value}
										onChangeFunc={(e) => {
											setAccessLevels((prevAccess) => ({
												...prevAccess,
												[val?.key]: e?.target?.checked ? val?.value : '',
											}));
										}}
									/>
									<BayTooltip
										overlay={
											<div style={{ width: '210px' }}>
												<div className="text-center">
													{t(val.tooltipContent)}
												</div>
												<div className="text-center">{t('useAccessGroup')}</div>
											</div>
										}
									>
										<span className="mx-2 cursor-pointer">
											<BsQuestionCircle />
										</span>
									</BayTooltip>
								</div>
							))}
							{isEmptyAccess && (
								<div className="ms-5 mt-3 text-danger">
									{t('validationAccessLevel')}
								</div>
							)}
						</>
					)}
				</Form.Group>
			</Row>
			<Row className="mt-0">
				<Form.Label className="text-900">{t('accessGroups')}</Form.Label>
			</Row>
			<Row className="mb-3">
				<Form.Label className="fw-normal mb-0">
					{isBanner ? t('chooseAccessGroupsBanner') : t('chooseAccessGroups')}
				</Form.Label>
				<Form.Group as={Col} className="mb-3">
					<div className="gap-2 flex-row align-items-center">
						<BayCheckbox
							className="mt-3"
							id="all_Groups"
							isChecked={!isSelectGroups}
							isRadio
							label={t('all')}
							name="accessGroups"
							onChangeFunc={() => setIsSelectGroups(false)}
						/>
						<BayCheckbox
							className="mt-2 pt-1"
							id="selectGroups"
							isChecked={isSelectGroups}
							isRadio
							label={t('selectGroups')}
							name="accessGroups"
							onChangeFunc={() => setIsSelectGroups(true)}
						/>
						{isSelectGroups && (
							<BaySelect
								isMulti
								className="mt-3"
								defaultValue={selectedAccessGroupArr}
								id="groupMembers"
								isClearable={false}
								name="groupMembers"
								optionsArray={groupList}
								placeholder={t('select')}
								onChangeFunc={(selectedValue, meta) =>
									handleSelectLimiters(meta.name, selectedValue)
								}
							/>
						)}
					</div>
				</Form.Group>
			</Row>
			<Row className="mt-0">
				<Form.Label className="text-900">{t('officeLocations')}</Form.Label>
			</Row>
			<Row className="my-0">
				<Form.Label className="fw-normal mb-0">
					{isBanner ? t('chooseOfficeLocationsBanner') : t('chooseOfficeLocations')}
				</Form.Label>
				<Form.Group as={Col} className="mb-3">
					<div className="gap-2 flex-row align-items-center pb-4">
						<BayCheckbox
							className="mt-3"
							id="all_Office"
							isChecked={!isSelectOffices}
							isRadio
							label={t('all')}
							name="officeLocation"
							onChangeFunc={() => setIsSelectOffices(false)}
						/>
						<BayCheckbox
							className="mt-2 pt-1"
							id="selectOffice"
							isChecked={isSelectOffices}
							isRadio
							label={t('selectOffices')}
							name="officeLocation"
							onChangeFunc={() => setIsSelectOffices(true)}
						/>
						{isSelectOffices && (
							<BaySelect
								isMulti
								className="mt-3"
								defaultValue={selectedOfficesArr}
								id="members"
								isClearable={false}
								name="members"
								optionsArray={officeList}
								placeholder={t('select')}
								onChangeFunc={(selectedValue, meta) =>
									handleSelectLimiters(meta.name, selectedValue)
								}
							/>
						)}
					</div>
				</Form.Group>
			</Row>
		</div>
	);
};

export default BayLimiters;
